/* Enhance native size selects while preserving their form values and change events. */
(() => {
  const selector = 'select[data-cherry-size-select]';
  const pickers = new WeakMap();
  let active;
  let nextId = 0;

  function enhance(select) {
    if (pickers.has(select)) return;
    const picker = document.createElement('div');
    picker.className = 'cherry-product__size-picker';
    select.before(picker);
    picker.append(select);
    select.classList.add('cherry-product__size');
    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'cherry-product__size cherry-product__size-toggle';
    toggle.id = `CherrySizeToggle-${++nextId}`;
    toggle.setAttribute('aria-expanded', 'false');
    const menu = document.createElement('div');
    menu.className = 'cherry-product__size-menu';
    menu.id = `CherrySizeMenu-${nextId}`;
    menu.hidden = true;
    menu.setAttribute('role', 'group');
    toggle.setAttribute('aria-controls', menu.id);
    const labels = [...select.labels];
    const label = labels.map(label => label.textContent.trim()).join(' ') || select.getAttribute('aria-label') || 'Size';
    menu.setAttribute('aria-label', label);
    labels.forEach(label => label.htmlFor = toggle.id);
    picker.append(toggle, menu);
    select.hidden = true;
    let rows = [];

    function close(restoreFocus = false) {
      menu.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
      if (active?.picker === picker) active = undefined;
      if (restoreFocus) toggle.focus();
    }

    function sync() {
      const selected = select.selectedOptions[0];
      toggle.textContent = selected?.dataset.label || selected?.textContent.trim() || 'Select Size';
      toggle.setAttribute('aria-label', `${label}: ${toggle.textContent}`);
      toggle.disabled = select.disabled;
      if (select.disabled) close();
      const options = [...select.options].filter(option => option.value !== '');
      if (rows.length !== options.length || rows.some((row, index) => row.dataset.value !== options[index].value)) {
        rows = options.map(option => {
          const row = document.createElement('button');
          row.type = 'button';
          row.className = 'cherry-product__size-option';
          row.dataset.value = option.value;
          const name = document.createElement('span');
          name.className = 'cherry-product__size-name';
          const status = document.createElement('span');
          status.className = 'cherry-product__size-notify';
          row.append(name, status);
          return row;
        });
        menu.replaceChildren(...rows);
      }
      rows.forEach((row, index) => {
        const option = options[index];
        const soldOut = option.dataset.available === 'false';
        row.firstChild.textContent = option.dataset.label || option.textContent.trim();
        row.lastChild.textContent = select.dataset.cherrySizeNotify !== undefined ? 'Notify me' : 'Sold out';
        row.lastChild.hidden = !soldOut;
        row.disabled = option.disabled;
        row.classList.toggle('is-sold-out', soldOut);
        row.setAttribute('aria-pressed', String(option.selected));
      });
    }

    function open(keyboard = false) {
      sync();
      if (toggle.disabled) return;
      active?.close();
      menu.hidden = false;
      toggle.setAttribute('aria-expanded', 'true');
      active = { picker, close };
      if (keyboard) {
        const enabled = rows.filter(row => !row.disabled);
        (keyboard === 'last' ? enabled[enabled.length - 1] : enabled.find(row => row.dataset.value === select.value) || enabled[0])?.focus();
      }
    }

    toggle.addEventListener('click', () => menu.hidden ? open(false) : close());
    menu.addEventListener('click', event => {
      const row = event.target.closest('button');
      if (!row || row.disabled || select.disabled) return;
      select.value = row.dataset.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      toggle.removeAttribute('aria-invalid');
      sync();
      close(false);
    });
    select.addEventListener('change', sync);
    select.addEventListener('invalid', event => {
      event.preventDefault();
      toggle.setAttribute('aria-invalid', 'true');
      open();
    });
    picker.addEventListener('keydown', event => {
      if (event.key === 'Escape' && !menu.hidden) {
        event.preventDefault();
        event.stopPropagation();
        close(true);
        return;
      }
      if (event.key === 'Tab') {
        close(false);
        return;
      }
      if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      if (menu.hidden) { open(event.key === 'ArrowUp' || event.key === 'End' ? 'last' : true); return; }
      const enabled = rows.filter(row => !row.disabled);
      const index = enabled.indexOf(document.activeElement);
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? enabled.length - 1 : (index + (event.key === 'ArrowDown' ? 1 : -1) + enabled.length) % enabled.length;
      enabled[next]?.focus();
    });
    picker.addEventListener('focusout', event => {
      if (!event.relatedTarget || event.relatedTarget === document.body || event.relatedTarget === document.documentElement) return;
      if (picker.contains(event.relatedTarget)) return;
      requestAnimationFrame(() => {
        if (document.activeElement && !picker.contains(document.activeElement)) close();
      });
    });
    pickers.set(select, sync);
    sync();
  }

  const scan = root => {
    if (root.matches?.(selector)) enhance(root);
    root.querySelectorAll?.(selector).forEach(enhance);
  };
  scan(document);
  // Quick views and theme editor sections are inserted after initial page load.
  new MutationObserver(mutations => {
    const changed = new Set();
    mutations.forEach(mutation => {
      mutation.addedNodes.forEach(node => { if (node.nodeType === 1) scan(node); });
      const select = mutation.target.closest?.(selector);
      if (select) changed.add(select);
    });
    changed.forEach(select => pickers.get(select)?.());
    if (active && !active.picker.isConnected) active.close();
  }).observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled', 'selected', 'data-available', 'data-label'] });
  document.addEventListener('change', event => {
    event.target.closest('form')?.querySelectorAll(selector).forEach(select => pickers.get(select)?.());
  });
  document.addEventListener('reset', event => {
    queueMicrotask(() => event.target.querySelectorAll(selector).forEach(select => pickers.get(select)?.()));
  });
  const outsideClick = event => { if (active && !active.picker.contains(event.target)) active.close(); };
  document.addEventListener('click', outsideClick);
  document.addEventListener('pointerdown', outsideClick);
})();
